import type { UseQueryResult, UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import type { ILLMWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

/**
 * Base query key for all LLM tracker data queries.
 * This constant is used as the root of the hierarchical query key structure.
 */
const LLM_TRACKER_DATA_QUERY_KEY = "llm-tracker-data";

/**
 * Query key factory for LLM Tracker data.
 *
 * This factory provides a hierarchical structure for React Query keys that enables
 * simple and efficient cache invalidation without needing complex predicate functions.
 *
 * Structure:
 * - Campaign level: ["llm-tracker-data", campaignGuid]
 * - Specific report: ["llm-tracker-data", campaignGuid, reportName, { filters }]
 *
 * Invalidation examples:
 * - Invalidate all data for a campaign: queryClient.invalidateQueries({ queryKey: llmTrackerQueryKeys.campaign(campaignId) })
 * - Invalidate specific report: queryClient.invalidateQueries({ queryKey: llmTrackerQueryKeys.report(campaignId, "brandOverview", filters) })
 */
export const llmTrackerQueryKeys = {
    /**
     * Query key for all data related to a specific campaign
     * @param campaignGuid - The unique identifier for the campaign
     */
    campaign: (campaignGuid: string) => [LLM_TRACKER_DATA_QUERY_KEY, campaignGuid] as const,

    /**
     * Query key for a specific report with filters
     * @param campaignGuid - The unique identifier for the campaign
     * @param reportName - The name of the report (e.g., "brandOverview", "topicsSummaryTable")
     * @param filters - Additional parameters excluding campaignGuid (startDate, endDate, scrapingConfigurationGuids, and any other params specific to the report)
     */
    report: (campaignGuid: string, reportName: string, filters: Record<string, unknown>) =>
        [...llmTrackerQueryKeys.campaign(campaignGuid), reportName, filters] as const,
};

export const useLLMReportQuery = <TRes, TParam extends ILLMWidgetRequestParams>(
    key: string,
    apiClientFn: (params: TParam) => Promise<TRes>,
    params: TParam,
    options?: Partial<UseQueryOptions<TRes>>,
): UseQueryResult<TRes> => {
    const { campaignGuid, ...filters } = params;
    const queryKey = llmTrackerQueryKeys.report(campaignGuid ?? "", key, filters);

    return useQuery<TRes>({
        queryKey,
        queryFn: () => apiClientFn(params),
        throwOnError: (error) => {
            Sentry.errorTracker({
                e: error,
                message: `Error fetching LLM tracker data for report: ${key}`,
                level: "error",
                params: { extra: { ...(params as Record<string, unknown>), queryKey } },
            });

            return false;
        },
        ...options,
    });
};
